import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import ApplicationForm from './ApplicationForm';
import ApplicationReview from './ApplicationReview';
import { get, del } from '../utils/api';

const DRAFT_STORAGE_KEY_PREFIX = 'marriage_app_draft_';
const TOTAL_STEPS = 16;

const Dashboard = ({ user, onLogout }) => {
  const [showApplication, setShowApplication] = useState(false);
  const [reviewingApplicationId, setReviewingApplicationId] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDraftStep, setPendingDraftStep] = useState(null);

  // Fetch user's applications on component mount and when returning to dashboard
  useEffect(() => {
    // Only fetch when we're viewing the dashboard (not the application form)
    if (showApplication || reviewingApplicationId) return;

    const fetchApplications = async () => {
      try {
        const data = await get('/api/applications');
        // Filter out deleted applications
        const activeApplications = (data.applications || data || []).filter(app => app.status !== 'deleted');
        setApplications(activeApplications);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [showApplication, reviewingApplicationId]);

  // Check localStorage for in-progress draft (same key as ApplicationForm); re-run when returning from form
  useEffect(() => {
    const key = user?.id ?? user?.email;
    if (!key) return;
    const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}${key}`;

    // If user has NO applications at all (all were deleted), also clear localStorage
    // This handles the case where a user deletes all their applications
    if (!loading && applications.length === 0) {
      const stored = localStorage.getItem(draftKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          // If the draft references an applicationId (was submitted), clear it
          // Otherwise keep it (it's a fresh draft the user is working on)
          if (parsed.formData?.applicationId) {
            localStorage.removeItem(draftKey);
            setPendingDraftStep(null);
            return;
          }
        } catch {
          // If we can't parse it, clear it to be safe
          localStorage.removeItem(draftKey);
          setPendingDraftStep(null);
          return;
        }
      }
    }

    try {
      const stored = localStorage.getItem(draftKey);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Check if localStorage references a deleted/non-existent application
        if (parsed.formData?.applicationId) {
          const appExists = applications.some(app =>
            (app.id || app.application_id) === parsed.formData.applicationId
          );

          // If the application doesn't exist anymore (was deleted), clear the draft
          if (!appExists) {
            localStorage.removeItem(draftKey);
            setPendingDraftStep(null);
            return;
          }
        }

        if (parsed.currentStep != null && parsed.formData) {
          setPendingDraftStep(Math.max(0, Math.min(parsed.currentStep, TOTAL_STEPS - 1)));
        } else {
          setPendingDraftStep(null);
        }
      } else {
        setPendingDraftStep(null);
      }
    } catch {
      setPendingDraftStep(null);
    }
  }, [user?.id, user?.email, showApplication, applications, loading]);

  // Helper function to check if an application is expired (60+ days old and still submitted/under_review)
  const isApplicationExpired = (app) => {
    if (app.status !== 'submitted' && app.status !== 'under_review') {
      return false;
    }

    try {
      const createdDate = new Date(app.created_at);
      const now = new Date();
      const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      return daysDiff > 60;
    } catch (e) {
      return false;
    }
  };

  // Helper function to get party names from application form_data
  const getPartyNames = (app) => {
    try {
      if (!app.form_data) return null;

      let formData = app.form_data;
      if (typeof formData === 'string') {
        formData = JSON.parse(formData);
      }

      // Handle wrapped format { currentStep, formData }
      const actualFormData = formData?.formData || formData;

      const partyA = actualFormData?.partyA || {};
      const partyB = actualFormData?.partyB || {};

      const getFullName = (party) => {
        const first = party.firstName || party.first_name || '';
        const middle = party.middleName || party.middle_name || '';
        const last = party.lastName || party.last_name || '';
        const suffix = party.suffix || '';
        const parts = [first, middle, last, suffix].filter(Boolean);
        return parts.join(' ') || 'Not provided';
      };

      return {
        partyA: getFullName(partyA),
        partyB: getFullName(partyB)
      };
    } catch (e) {
      return null;
    }
  };

  const getNextStep = (app) => {
    if (isApplicationExpired(app) || app.status === 'rejected') return 'Start a new application or contact the Marriage License office.';
    if (app.status === 'submitted' || app.status === 'under_review') return 'Await clerk review, then bring your original documents to the office.';
    if (app.status === 'approved') return 'Visit either Clerk of Court office with both parties, original documents, and payment.';
    if (app.status === 'completed') return 'Your license process is complete.';
    return 'Contact the Marriage License office for the next step.';
  };

  // If reviewing a submitted application, show ApplicationReview
  if (reviewingApplicationId) {
    return (
      <ApplicationReview
        applicationId={reviewingApplicationId}
        user={user}
        onExit={() => setReviewingApplicationId(null)}
      />
    );
  }

  // If application is open, show ApplicationForm instead of dashboard
  if (showApplication) {
    return (
      <ApplicationForm
        user={user}
        onExit={() => setShowApplication(false)}
      />
    );
  }

  // Otherwise show regular dashboard
  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon">
              <img
                src={`${process.env.PUBLIC_URL}/JPCC_seal_trans_white.png`}
                alt="Jefferson Parish Clerk of Court Seal"
              />
            </div>
            <div className="logo-text">
              <h1>Jon A. Gegenheimer</h1>
              <p>Jefferson Parish Clerk of Court</p>
              <p className="logo-context">Marriage License Pre-Application</p>
            </div>
          </div>
          
          <div className="user-menu">
            <div className="user-info">
              <span className="user-name">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.firstName || user?.email}
              </span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button onClick={onLogout} className="logout-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="container">
          <div className="welcome-section">
            <h2>Welcome back, {user?.firstName || 'Guest'}!</h2>
            <p>Complete your marriage license application online, then visit our office in person to finalize.
              Marriage licenses issued in Louisiana may be used anywhere within Louisiana and are valid for 30 days from the date issued by the Clerk of Court. 
              Officiants are responsible for returning the original, completed license to the appropriate Clerk of Court immediately following the marriage ceremony. 
              Thereafter, the parties may purchase certified true copies of the recorded license from the Clerk of Court.
            </p>
          </div>

          <div className="info-banner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4>In-Person Visit Required</h4>
              <p>After submitting your online application, you must visit our office with valid identification and supporting documents. Payment will be collected at that time.</p>
            </div>
          </div>

          {/* Status boxes only for non-draft applications (draft uses the single card below) */}
          {!loading && applications.filter((app) => app.status !== 'draft').length > 0 && (
            <div className="application-status-container">
              {applications.filter((app) => app.status !== 'draft').map((app) => (
                <div key={app.id || app.application_id} className="application-status-box">
                  <div className="status-header">
                    <h3>Your Marriage License Application</h3>
                    <span className={`status-badge status-${
                      isApplicationExpired(app) ? 'rejected' :
                      (app.status === 'submitted' || app.status === 'under_review') ? 'submitted' :
                      (app.status === 'approved' || app.status === 'completed') ? 'approved' :
                      app.status === 'rejected' ? 'rejected' : app.status
                    }`}>
                      {isApplicationExpired(app) ? 'Expired' :
                       (app.status === 'submitted' || app.status === 'under_review') ? 'Submitted' :
                       (app.status === 'approved' || app.status === 'completed') ? 'Approved' :
                       app.status === 'rejected' ? 'Rejected' : app.status}
                    </span>
                  </div>
                  <div className="status-content">
                    <div className="next-step-panel"><strong>Your next step</strong><p>{getNextStep(app)}</p></div>
                    {(app.status === 'submitted' || app.status === 'under_review' || app.status === 'approved') && (
                      <div className="document-reminder"><strong>Bring:</strong> photo IDs, certified birth certificates, Social Security information or waiver, and prior-marriage documents if applicable. Payment is collected in person.</div>
                    )}
                    {(() => {
                      const names = getPartyNames(app);
                      return names ? (
                        <div className="status-note">
                          <p style={{ margin: '0 0 0.5rem 0' }}><strong>Party A:</strong> {names.partyA}</p>
                          <p style={{ margin: 0 }}><strong>Party B:</strong> {names.partyB}</p>
                        </div>
                      ) : null;
                    })()}
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setReviewingApplicationId(app.id || app.application_id)}
                      >
                        View Application
                      </button>
                      {(app.status === 'submitted' || app.status === 'under_review' || app.status === 'approved') && (
                        <a className="withdrawal-link" href="mailto:marriageinfo@jpclerkofcourt.us?subject=Marriage%20license%20application%20withdrawal%20request">Request withdrawal</a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Show New/Pending Application card */}
          {!loading && (() => {
            // Show "Continue Application" if there's a pending draft, otherwise show "Start New Application"
            const shouldShowContinue = pendingDraftStep != null;

            const handleStartApplication = async () => {
              // If starting a new application (not continuing), clear localStorage and backend drafts
              if (!shouldShowContinue) {
                const key = user?.id ?? user?.email;
                if (key) {
                  const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}${key}`;
                  localStorage.removeItem(draftKey);
                  setPendingDraftStep(null);
                }

                // Delete all existing drafts from backend to ensure fresh start
                try {
                  await del('/api/applications/drafts/all');
                } catch (error) {
                  console.error('Error deleting drafts:', error);
                  // Continue anyway - the frontend will create a new draft
                }
              }
              setShowApplication(true);
            };

            const handleStartOver = async () => {
              const confirmed = window.confirm(
                'Are you sure you want to start over? Your current progress will be lost.'
              );
              if (!confirmed) return;

              const key = user?.id ?? user?.email;
              if (key) {
                const draftKey = `${DRAFT_STORAGE_KEY_PREFIX}${key}`;
                localStorage.removeItem(draftKey);
                setPendingDraftStep(null);
              }
              try {
                await del('/api/applications/drafts/all');
              } catch (error) {
                console.error('Error deleting drafts:', error);
              }
              setShowApplication(true);
            };

            return (
              <div className="dashboard-grid application-action-grid">
                <div className="card application-action-card">
                  <div className="card-icon new-application">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      {shouldShowContinue ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      )}
                    </svg>
                  </div>
                  <h3>{shouldShowContinue ? 'Continue Application' : 'Start New Application'}</h3>
                  <p>
                    {shouldShowContinue ? (
                      <>
                        You have an application in progress. Continue where you left off.
                        <br />
                        <strong>Step {pendingDraftStep + 1} of {TOTAL_STEPS}</strong>
                      </>
                    ) : (
                      'Start a new marriage license application.'
                    )}
                  </p>
                  {shouldShowContinue ? (
                    <div className="application-action-buttons">
                      <button
                        className="application-action-secondary"
                        onClick={handleStartOver}
                      >
                        Start Over
                      </button>
                      <button
                        className="application-action-primary"
                        onClick={handleStartApplication}
                      >
                        Continue
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      className="card-button"
                      onClick={handleStartApplication}
                    >
                      Start New Application
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="info-section">
            <h3>What You'll Need</h3>
            <div className="requirements-grid">
              <div className="requirement-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4>Complete Online Application</h4>
                  <p>Here you will prepare and submit your marriage license application, which will be stored in our system for 90 days. When you arrive at the courthouse, our clerks will retrieve your info and issue the marriage license. This convenient process will provide you with information regarding documents you will need to present and fees to be submitted, and will reduce your time spent at the courthouse.</p>
                </div>
              </div>
              <div className="requirement-item">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4>Bring to Office Visit</h4>
                  <ul>
                    <li>Valid identification according to La. R.S. 9:224 et seq.</li>
                    <li>Certified true copies of birth certificates (or signed waiver).</li>
                    <li>Social Security Numbers for both parties according to La. R.S. 9:224 / La. Civil Code Article 74.3.1 (or waiver statement)</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="office-locations">
              <h4>Office Locations</h4>
              <div className="locations-grid">
                <div className="location-card">
                  <h5>West Bank Office</h5>
                  <p>200 Derbigny St., Suite 2200<br/>Gretna, LA 70053</p>
                  <p className="phone">(504) 364-2922</p>
                  <p className="hours">Mon-Fri: 8:30 AM - 4:30 PM</p>
                </div>
                <div className="location-card">
                  <h5>East Bank Office</h5>
                  <p>1221 Elmwood Park Blvd., Suite 603<br/>Jefferson, LA 70123</p>
                  <p className="phone">(504) 736-6390</p>
                  <p className="hours">Mon-Fri: 8:30 AM - 4:30 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
